// The survey on the room cards (SURVEY_CARDS_C1_WORDS.md, MOCKUP_SURVEY_CARDS.html approved 2026-10-29). Pure: state
// in, HTML out. Taps use data-cd; the card's fields, photos and Opened keep Collect's data-cl, so they save the way
// they always have. The look is Field Labels': You Are In on the left, the room's gear as cards on the right.

import { esc } from "./training-screens";
import { panelHtml, sectionGrid, type DrawingView } from "./field-labels-screens";
import { roomLetters, type SiteRoom } from "./field-labels-site";
import { bucketCard, cardBody, type CollectPanelState } from "./survey-collect-screens";
import { cardFor, cardStatus, finished, recOf, wireOf, wireStatus, wireTag, type Site, type Status } from "./survey-collect";
import { codeOf, shortName, type MapLink, type Registry } from "./survey-map";
import { cardLight, cardLine, cardRows, gearFromIds, lettersFromIds, roomLight, rowLight, takesFeed, type CardItem, type CardRoom } from "./survey-cards";

export type CardsSheet =
  | { kind: "rooms" }
  /** Fed From for an asset: pick the room, then the gear, then where on it (item 11). */
  | { kind: "fed"; asset: string; room: string | null; from: string | null };

export interface CardsView {
  siteNumber: string;
  reg: Registry;
  site: Site;
  rooms: readonly CardRoom[];
  room: string | null;
  open: string | null;
  /** The breaker or bucket open on the drawing: its key ("11", "A2"). */
  row: string | null;
  sheet: CardsSheet | null;
  panel: Omit<CollectPanelState, "cur">;
  /** The walk's room, for its drawing letters. */
  walkRoom: (room: string) => SiteRoom | null;
  feed: (assetId: string) => MapLink | null;
  feedWords: (l: MapLink) => string;
  /** What Fed From offers: rooms, the gear in the room picked, the points on the gear picked. */
  fedRooms: () => string[];
  fedGear: (asset: string, room: string) => string[];
  fedPoints: (feeder: string) => { key: string; name: string }[];
}

const dot = (s: Status, extra = "") => `<span class="cl-dot ${s}${extra}" aria-hidden="true"></span>`;
const plural = (n: number, w: string) => `${n} ${w}${n === 1 ? "" : "s"}`;

/** The page under the survey's header. */
export function cardsScreen(v: CardsView): string {
  const room = v.rooms.find((r) => r.room === v.room) ?? v.rooms[0] ?? null;
  if (!room) return `<div class="sc-empty">Nothing on the Assessment Sheet for this site yet.<br>Push each room from Field Labels, fill the rows on the sheet, then the cards come in here.</div>`;
  const done = room.items.filter((i) => finished(cardLight(v.site, i))).length;
  const left = `<div><div class="fl-hd">You Are In</div><button class="fl-roombtn" type="button" data-cd="rooms">${dot(roomLight(v.site, room), " sc-rlight")}<b>${esc(room.room)}</b><small>${plural(room.items.length, "piece")} of gear<br>Tap to change room</small></button></div>
    <div class="sc-roomnote">${done} of ${room.items.length} done</div>`;
  const cards = room.items.map((i) => (i.assetId === v.open ? openCard(v, room, i) : closedCard(v, i))).join("");
  const right = `<div class="fl-rhd"><b>${esc(room.room)}</b><span>Hanging order, top to bottom</span></div>${cards}`;
  return `<div class="fl-page sc-page"><div class="fl-main sc-main"><div class="fl-left" id="sc-left">${left}</div><div class="fl-right" id="sc-right">${right}</div></div></div>${sheetHtml(v)}`;
}

/**
 * The Drawing, Locked (BUILD_LOCK_WORDS.md item 13): the same open card as here, for its left panel, with its Fed From
 * sheet. Its taps keep data-cd and data-cl, so it saves exactly the way the card saves. Null when it has no card.
 */
export function drawingCard(v: CardsView, assetId: string): { card: string; sheet: string } | null {
  for (const room of v.rooms) {
    const i = room.items.find((x) => x.assetId === assetId);
    if (i) return { card: openCard(v, room, i), sheet: sheetHtml(v) };
  }
  return null;
}

function closedCard(v: CardsView, i: CardItem): string {
  const a = v.reg.byId.get(i.assetId);
  const name = a ? shortName(a) : i.assetId;
  const photos = photoCount(v.site, i);
  return `<div class="fl-card done sc-card" data-sc-a="${esc(i.assetId)}"><div class="fl-chead">${dot(cardLight(v.site, i), " sc-dot")}<button class="fl-lab sm" type="button" data-cd="open" data-cd-v="${esc(i.assetId)}">${esc(name)}</button><span class="fl-meta">${esc(cardLine(v.site, v.reg, i))}${photos ? ` · ${plural(photos, "photo")}` : ""}</span><button class="fl-tog fl-edit" type="button" data-cd="open" data-cd-v="${esc(i.assetId)}">Open</button></div></div>`;
}

/** Photos on the asset and its rows. */
export function photoCount(site: Site, i: CardItem): number {
  return Object.keys(site.recs[i.assetId]?.photos ?? {}).length + cardRows(site, i).reduce((n, x) => n + Object.keys(x.rec.photos).length, 0);
}

function openCard(v: CardsView, room: CardRoom, i: CardItem): string {
  const a = v.reg.byId.get(i.assetId);
  const name = a ? shortName(a) : i.assetId;
  const s: CollectPanelState = { ...v.panel, cur: null };
  const top = `<div class="sc-top"><button class="fl-lab" type="button" data-cd="close">${esc(name)}</button>${fedButtons(v, i.assetId)}<span class="sc-gap"></span><button class="fl-hdone" type="button" data-cd="close">Done</button></div>`;
  const rows = cardRows(v.site, i);
  const drawing = drawingHtml(v, room, i);
  const rowCard = v.row && rows.some((r) => r.key === v.row) ? rowHtml(v, s, i, v.row, rows) : "";
  const body = cardBody(v.site, s, i.assetId);
  if (!drawing) return `<div class="fl-card cur sc-open" data-sc-a="${esc(i.assetId)}">${top}<div class="sc-one">${body}</div></div>`;
  const word = (rows.length && /^\d+$/.test(rows[0]!.key)) || isPanelAsset(v.reg, i.assetId) ? "Breakers" : "Buckets";
  // A bucket or breaker open: its card leads and the housing folds to one line, so nothing is scrolled past.
  const housing = rowCard
    ? `<button type="button" class="cl-fold1 sc-hfold" data-cd="row" data-cd-v="${esc(v.row ?? "")}">${dot(cardStatus(cardFor(v.site, i.assetId), recOf(v.site, i.assetId)))}<b>Housing Card</b><span>Tap to go back to the housing</span></button>`
    : body;
  return `<div class="fl-card cur sc-open" data-sc-a="${esc(i.assetId)}">${top}<div class="sc-cols"><div class="sc-colL">${rowCard ? `${housing}${rowCard}` : housing}</div><div class="sc-colR"><div class="cl-sec">${word} · ${rows.length} · Tap One</div>${drawing}</div></div></div>`;
}

/** Fed From and Wire on the card's top line (items 11 and 12). */
function fedButtons(v: CardsView, assetId: string): string {
  if (!takesFeed(v.reg, assetId)) return `<span class="sc-fed src"><small>Fed From</small><b>Source</b></span>`;
  const l = v.feed(assetId);
  const fed = `<button class="sc-fed${l ? "" : " need"}" type="button" data-cd="fed" data-cd-v="${esc(assetId)}"><small>Fed From</small><b>${l ? esc(v.feedWords(l)) : "&nbsp;"}</b></button>`;
  // No feed, no wire: the Wire In button shows once Fed From is set (SURVEY_DRAWING_IDS_C1_WORDS.md item 13).
  if (!l) return fed;
  const w = wireOf(v.site, l.id);
  const st = wireStatus(w);
  return `${fed}<button class="sc-fed${st === "done" ? "" : " need"}" type="button" data-cl="wire" data-cl-link="${esc(l.id)}"><small>Wire In</small><b>${st === "none" ? "&nbsp;" : esc(wireTag(w))}</b></button>`;
}

/** The walk's drawing beside the card, lights on, for tapping (item 9). Gear the walk never drew gets the table. */
function drawingHtml(v: CardsView, room: CardRoom, i: CardItem): string {
  const view: DrawingView = { act: "row", light: (key) => rowLight(v.site, i, key), picked: v.row };
  const g = i.gear;
  const wr = v.walkRoom(room.room);
  // Sectioned gear (an MCC, a switchboard with compartments) draws its sections; a panel its two sides.
  if (g && wr && g.sections.length) return `<div class="${g.sections.length > 7 ? "sc-tight" : ""}" style="--sc-n:${g.sections.length}">${sectionGrid(wr, g, roomLetters(wr), null, view)}</div>`;
  if (g && Object.keys(g.circuits).length) return panelHtml(g, null, view);
  const rows = cardRows(v.site, i);
  if (!rows.length) return "";
  // No walk drawing (gear from the sheet): the front built from its Asset IDs (SURVEY_DRAWING_IDS_C1_WORDS.md).
  const keys = rows.map((r) => r.key);
  const ids = gearFromIds(keys);
  if (ids) {
    const wr0: SiteRoom = { name: room.room, gear: [ids] };
    return `<div class="${ids.sections.length > 7 ? "sc-tight" : ""}" style="--sc-n:${ids.sections.length}">${sectionGrid(wr0, ids, new Map([[ids.id, lettersFromIds(keys)]]), null, view)}</div>`;
  }
  if (isPanelAsset(v.reg, i.assetId)) return canByCount(rows, view);
  // Positions that make no clean front (a gap, a stray name): the list, each with its light.
  return `<div class="sc-list">${rows.map((r) => `<button type="button" class="fl-tog sc-rowbtn${v.row === r.key ? " on" : ""}" data-cd="row" data-cd-v="${esc(r.key)}">${dot(rowLight(v.site, i, r.key))}${esc(r.name)}</button>`).join("")}</div>`;
}

/**
 * A panel from the sheet with no walk drawing: the can, MB on top, then its breakers by count, down the left side
 * and down the right, either side of the bus. No circuit numbers: the sheet does not carry them.
 */
function canByCount(rows: { key: string; name: string }[], view: DrawingView): string {
  const mb = rows.filter((r) => r.key === "MB");
  const rest = rows.filter((r) => r.key !== "MB");
  const half = Math.ceil(rest.length / 2);
  const btn = (r: { key: string; name: string }, row: number, col: number) =>
    `<button type="button" class="fl-pbrk one${view.picked === r.key ? " flash" : ""} fl-vbtn" data-cd="${esc(view.act)}" data-cd-v="${esc(r.key)}" style="grid-row:${row};grid-column:${col}" aria-label="${esc(r.name)}">${drawLight(view.light(r.key))}${esc(r.name)}</button>`;
  let cells = rest.length ? `<div class="fl-pbus" style="grid-row:1 / ${half + 1}"></div>` : "";
  rest.forEach((r, k) => (cells += k < half ? btn(r, k + 1, 1) : btn(r, k - half + 1, 5)));
  const main = mb.map((r) => `<button type="button" class="fl-pmb fl-vbtn${view.picked === r.key ? " flash" : ""}" data-cd="${esc(view.act)}" data-cd-v="MB" aria-label="MB, Main Breaker">${drawLight(view.light("MB"))}MB · Main Breaker</button>`).join("");
  return `${main}${rest.length ? `<div class="fl-can">${cells}</div>` : ""}`;
}

/** A panel by its code (PNL): its rows are breakers, drawn on the can. */
const isPanelAsset = (reg: Registry, assetId: string): boolean => {
  const a = reg.byId.get(assetId);
  return Boolean(a) && codeOf(a!) === "PNL";
};

const drawLight = (s: Status) => `<span class="cl-dot ${s} fl-vdot" aria-hidden="true"></span>`;

/** The open breaker or bucket: Collect's own row card, under the housing, with Next Bucket. */
function rowHtml(v: CardsView, s: CollectPanelState, i: CardItem, key: string, rows: { key: string; name: string }[]): string {
  const at = rows.findIndex((r) => r.key === key);
  const next = rows[at + 1];
  const name = rows[at]?.name ?? key;
  const word = /^\d+$/.test(key) || isPanelAsset(v.reg, i.assetId) ? "Breaker" : "Bucket";
  // The survey keeps a panel breaker by its lowest circuit; the card names it the way the walk counts it.
  const card = bucketCard(v.site, s, i.assetId, key).replace(`Breaker ${key} · stored on the panel`, `${name} · circuit ${key}`);
  return `<div class="sc-row"><div class="cl-sec">${esc(word)} ${esc(name)}</div>${card}${
    next ? `<button class="sv-btn line sm sc-next" type="button" data-cd="row" data-cd-v="${esc(next.key)}">Next ${esc(word)} · ${esc(next.name)}</button>` : ""
  }</div>`;
}

// ---- Sheets ----

function sheetHtml(v: CardsView): string {
  const sh = v.sheet;
  if (!sh) return "";
  if (sh.kind === "rooms") {
    const opts = v.rooms
      .map((r) => `<button class="mp-opt${r.room === v.room ? " used" : ""}" type="button" data-cd="room" data-cd-v="${esc(r.room)}">${dot(roomLight(v.site, r))}<b>${esc(r.room)}</b><small>${plural(r.items.length, "piece")} of gear</small></button>`)
      .join("");
    return card("Rooms", "", opts, "");
  }
  const a = v.reg.byId.get(sh.asset);
  const title = `Fed From · ${a ? shortName(a) : sh.asset}`;
  if (!sh.room) {
    const opts = v.fedRooms().map((r) => `<button class="mp-opt" type="button" data-cd="fed-room" data-cd-v="${esc(r)}"><b>${esc(r)}</b></button>`).join("");
    return card(title, "Pick the room it comes from.", opts, "");
  }
  if (!sh.from) {
    const list = v.fedGear(sh.asset, sh.room);
    const opts = list.length
      ? list
          .map((id) => {
            const g = v.reg.byId.get(id);
            return `<button class="mp-opt" type="button" data-cd="fed-gear" data-cd-v="${esc(id)}"><b>${esc(g ? shortName(g) : id)}</b><small>${esc(g?.equipmentType ?? "")}</small></button>`;
          })
          .join("")
      : `<div class="mp-emptyopt">Nothing in ${esc(sh.room)} can feed it.</div>`;
    return card(title, `${sh.room} ›`, opts, `<button class="sv-btn line sm" type="button" data-cd="fed-back">Back</button>`);
  }
  const f = v.reg.byId.get(sh.from);
  const opts = v
    .fedPoints(sh.from)
    .map((p) => `<button class="mp-opt" type="button" data-cd="fed-point" data-cd-v="${esc(p.key)}"><b>${esc(p.name)}</b></button>`)
    .join("");
  return card(title, `${sh.room} › ${f ? shortName(f) : sh.from} ›`, opts, `<button class="sv-btn line sm" type="button" data-cd="fed-back">Back</button>`);
}

function card(title: string, sub: string, body: string, foot: string): string {
  return `<div class="mp-sheet" data-cd="sheet-bg"><div class="mp-card sc-sheet">
    <div class="mp-sheethd"><b>${esc(title)}${sub ? `<small>${esc(sub)}</small>` : ""}</b><button class="sv-btn line sm" type="button" data-cd="sheet-close">Cancel</button></div>
    <div class="mp-opts" id="sc-opts">${body}</div>${foot ? `<div class="mp-sheetft">${foot}</div>` : ""}
  </div></div>`;
}
